import React from "react";
import { useGlobalProvider } from "../../context/GlobalContext";
import {
  Menu,
  IconButton,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { Box } from "@mui/system";
import menuIcon from "@/assets/svg/menu.svg";

export type MenuDropDownSubMenuItem = {
  label: string;
  onClick: () => void;
};

export type MenuDropDownItem = {
  label: string;
  icon?: React.ReactNode;
  show?: boolean;
  onClick?: () => void;
  subMenuItems?: MenuDropDownSubMenuItem[];
};

interface MenuDropDownProps {
  children?: React.ReactNode;
  title?: string;
  tooltip?: string;
  menuItems: MenuDropDownItem[];
}

const menuPaperSx = {
  width: 250,
  borderRadius: "8px",
  boxShadow:
    "0px 1px 5px 0px rgba(44, 62, 80, 0.20), 0px 2px 2px 0px rgba(44, 62, 80, 0.14), 0px 3px 1px 0px rgba(44, 62, 80, 0.12)",
  backgroundColor: "#F7F7F9",
  border: "1px solid #E0E0E0",
  marginTop: "0px",
} as const;

const menuItemSx = {
  padding: "0px 16px",
  fontSize: "0.9rem",
  fontFamily: "Helvetica, Neue",
  color: "#0E4664",
  "&:hover": {
    backgroundColor: "rgba(0, 40, 60, 0.1)",
  },
} as const;

const menuListSx = {
  padding: "0px",
} as const;

const menuContainerSx = {
  "& .MuiMenu-list": menuListSx,
  "& .MuiPaper-root": menuPaperSx,
  "& .MuiMenuItem-root": menuItemSx,
} as const;

function menuItemBorderSx(index: number, total: number) {
  return {
    borderBottom: index < total - 1 ? "1px solid #E0E0E0" : "none",
    height: "40px",
  };
}

export const MenuDropDown: React.FC<MenuDropDownProps> = ({
  children,
  title,
  tooltip,
  menuItems,
}) => {
  const { menuOpen, setMenuOpen } = useGlobalProvider();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [subMenuAnchorEl, setSubMenuAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const [openSubMenuIndex, setOpenSubMenuIndex] = React.useState<number | null>(
    null,
  );

  const filteredMenuItems = menuItems.filter((item) => item.show !== false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const closeSubMenu = () => {
    setOpenSubMenuIndex(null);
    setSubMenuAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuOpen(false);
    closeSubMenu();
  };

  const handleSubMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setSubMenuAnchorEl(event.currentTarget);
    setOpenSubMenuIndex(index);
  };

  const handleMenuItemClick = (
    item: MenuDropDownItem,
    index: number,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    if (item.subMenuItems?.length) {
      event.stopPropagation();
      handleSubMenuOpen(event, index);
      return;
    }
    item.onClick?.();
    handleClose();
  };

  const activeSubMenuItems =
    openSubMenuIndex !== null
      ? (filteredMenuItems[openSubMenuIndex]?.subMenuItems ?? [])
      : [];

  const menuButton = (
    <IconButton
      onClick={handleClick}
      sx={{
        color: "#0E4664",
        p: 0.5,
        lineHeight: 0,
        "&:hover": {
          backgroundColor: "#e1efea",
        },
      }}
      disableRipple={true}
    >
      <Box
        component="img"
        src={menuIcon}
        alt="Menu"
        sx={{ width: 22, height: 22, display: "block" }}
      />
    </IconButton>
  );

  return (
    <>
      {tooltip ? (
        <Tooltip title={tooltip} arrow>
          <span>{menuButton}</span>
        </Tooltip>
      ) : (
        menuButton
      )}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={menuContainerSx}
      >
        {title && (
          <Box
            sx={{
              padding: "12px 16px",
              backgroundColor: "#182737",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          >
            {title}
          </Box>
        )}
        {filteredMenuItems.map((item, index) => (
          <MenuItem
            key={index}
            onClick={(event) => handleMenuItemClick(item, index, event)}
            onMouseEnter={(event) => {
              if (item.subMenuItems?.length) {
                handleSubMenuOpen(event, index);
              } else {
                closeSubMenu();
              }
            }}
            sx={{
              ...menuItemBorderSx(index, filteredMenuItems.length),
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              {item.icon && (
                <ListItemIcon sx={{ minWidth: 36, color: "#0E4664" }}>
                  {item.icon}
                </ListItemIcon>
              )}
              <ListItemText primary={item.label} />
            </Box>
            {item.subMenuItems?.length ? (
              <ChevronRight sx={{ fontSize: 18, color: "#0E4664", ml: 1 }} />
            ) : null}
          </MenuItem>
        ))}
        {children}
      </Menu>

      <Menu
        anchorEl={subMenuAnchorEl}
        open={openSubMenuIndex !== null && activeSubMenuItems.length > 0}
        onClose={closeSubMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            onMouseEnter: () => {
              /* keep open while pointer is over flyout */
            },
            onMouseLeave: closeSubMenu,
          },
        }}
        sx={{
          ...menuContainerSx,
          pointerEvents: "none",
          "& .MuiPaper-root": {
            ...menuPaperSx,
            pointerEvents: "auto",
          },
        }}
      >
        {activeSubMenuItems.map((subItem, subIndex) => (
          <MenuItem
            key={subIndex}
            onClick={() => {
              subItem.onClick();
              handleClose();
            }}
            sx={menuItemBorderSx(subIndex, activeSubMenuItems.length)}
          >
            <ListItemText primary={subItem.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
